import { html, type TemplateResult } from 'lit-html'
import {
  GAME_CONFIG,
  RELIC_GLYPH,
  TERRAIN_GLYPH,
  UNKNOWN_GLYPH,
  type Island,
  type api,
} from '@guild/core'

/**
 * The map: a CSS grid of fixed cells, never a `<pre>`.
 *
 * The glyphs fall back across fonts -- `♠ ▲ ⌂ ◆` are not all in IBM Plex Mono
 * (Q7.2) -- so the cells are sized by the grid and not by the character in
 * them. A `<pre>` would misalign the moment one glyph came from a different
 * face. The share card is the opposite case and is a `<pre>`, in ASCII.
 *
 * Coordinates are player-visible (#54): letters quote a position as `x, y`,
 * with x across 0..19 and y down 0..11, so the grid carries quiet markers in
 * its margins and every picker in the app counts the same way.
 */

const { width, height } = GAME_CONFIG.grid

/** A relic somebody else has staked. The staked-by-you glyph is `◆` (Q3.2). */
export const SPOKEN_FOR_GLYPH = '◇'
/** Where the player stands. */
export const YOU_GLYPH = '@'

/** Glyph to the class that colours it. Built from core's glyph set, not retyped. */
const GLYPH_CLASS: Readonly<Record<string, string>> = {
  [TERRAIN_GLYPH.water]: 't-water',
  [TERRAIN_GLYPH.sand]: 't-sand',
  [TERRAIN_GLYPH.grass]: 't-grass',
  [TERRAIN_GLYPH.forest]: 't-forest',
  [TERRAIN_GLYPH.mountain]: 't-mount',
  [TERRAIN_GLYPH.ruin]: 't-ruin',
  [RELIC_GLYPH]: 't-relic',
  [SPOKEN_FOR_GLYPH]: 't-relic-gone',
  [UNKNOWN_GLYPH]: 't-unknown',
  [YOU_GLYPH]: 't-you',
}

/** The class that colours a glyph, so the legend can show its ground in its colour (#131). */
export const glyphClass = (glyph: string): string => GLYPH_CLASS[glyph] ?? ''

/** The legend, in the order the prototype prints it. */
export const LEGEND: readonly { glyph: string; key: string }[] = [
  { glyph: TERRAIN_GLYPH.water, key: 'legendWater' },
  { glyph: TERRAIN_GLYPH.sand, key: 'legendSand' },
  { glyph: TERRAIN_GLYPH.grass, key: 'legendGrass' },
  { glyph: TERRAIN_GLYPH.forest, key: 'legendForest' },
  { glyph: TERRAIN_GLYPH.mountain, key: 'legendMountain' },
  { glyph: TERRAIN_GLYPH.ruin, key: 'legendRuin' },
  { glyph: RELIC_GLYPH, key: 'legendRelic' },
]

/** How a cell came to be what it is: the three ways a tile is known. */
export type CellSource = 'seen' | 'hearsay' | 'unknown'

export type Cell = {
  readonly x: number
  readonly y: number
  readonly glyph: string
  readonly source: CellSource
  /** True for the tile the player is standing on. */
  readonly you: boolean
  /** True for a landing shore the return trip may choose (Q21.1). */
  readonly shore?: boolean
}

const index = (x: number, y: number) => y * width + x

/**
 * The 240 cells of a player's map: what they have seen, what a letter claimed,
 * and fog everywhere else.
 *
 * Relic markers are drawn over the terrain, and the rule is Q15.5's: the
 * player's own stake shows as `◆` first, and `◇` only where somebody else has
 * staked a relic this player has not.
 */
export function cellsOf(island: api.ApiIslandState, options: { you?: boolean } = {}): Cell[] {
  const cells: Cell[] = []
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      cells.push({ x, y, glyph: UNKNOWN_GLYPH, source: 'unknown', you: false })
    }
  }

  for (const cell of island.cells) {
    cells[index(cell.x, cell.y)] = {
      x: cell.x,
      y: cell.y,
      glyph: cell.glyph,
      source: cell.source,
      you: false,
    }
  }

  for (const relic of island.relics) {
    const at = cells[index(relic.at.x, relic.at.y)]
    if (at === undefined) continue
    const glyph = relic.staked || !relic.spokenFor ? RELIC_GLYPH : SPOKEN_FOR_GLYPH
    cells[index(relic.at.x, relic.at.y)] = { ...at, glyph }
  }

  // Not always: on the sinking day, before the boat has put in, the player is
  // at sea and the position on the state is where yesterday left them (Q21.1).
  const standing =
    options.you === false ? undefined : cells[index(island.position.x, island.position.y)]
  if (standing !== undefined) {
    cells[index(island.position.x, island.position.y)] = {
      ...standing,
      glyph: YOU_GLYPH,
      you: true,
    }
  }

  return cells
}

/**
 * A whole island, revealed, as a picture (#136).
 *
 * Every tile as the ground it is, the ruins among them, and the relics drawn
 * over as they are drawn on a chart; nobody standing on it and nothing
 * staked. For the landing, which shows a visitor what an island looks like
 * before they have walked one -- the practice island, so the one they
 * practise on is the one they saw.
 */
export function cellsOfWhole(island: Island): Cell[] {
  const cells: Cell[] = island.tiles.map((terrain, at) => ({
    x: at % width,
    y: Math.trunc(at / width),
    glyph: TERRAIN_GLYPH[terrain],
    source: 'seen',
    you: false,
  }))
  for (const relic of island.relics) {
    const at = cells[index(relic.x, relic.y)]
    if (at !== undefined) cells[index(relic.x, relic.y)] = { ...at, glyph: RELIC_GLYPH }
  }
  return cells
}

/**
 * The shores a return landing may choose, drawn over the fog (Q21.1).
 *
 * A shore is sand, and it is ground this player may never have walked -- so it
 * is drawn as the ground it is, and marked as a thing to pick rather than as a
 * thing they have seen.
 */
export function withShores(cells: readonly Cell[], shores: api.ApiIslandState['shores']): Cell[] {
  const marked = [...cells]
  for (const shore of shores) {
    const at = marked[index(shore.at.x, shore.at.y)]
    if (at === undefined) continue
    marked[index(shore.at.x, shore.at.y)] = {
      ...at,
      glyph: TERRAIN_GLYPH.sand,
      source: 'seen',
      shore: true,
    }
  }
  return marked
}

export type Selection = {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

export type GridOptions = {
  /** A rectangle to outline, for the slice picker. */
  readonly selection?: Selection | undefined
  /** Called with the tile under a pointer press, move and release. */
  readonly onPoint?: ((x: number, y: number, phase: 'down' | 'move' | 'up') => void) | undefined
  /** Glyphs the sender has altered, by cell index into the selection. */
  readonly altered?: ReadonlyMap<string, string> | undefined
  readonly small?: boolean
  /**
   * A picture rather than a chart (#136): no column and row markers, because
   * nothing on it is to be read as a coordinate. The cells, the colours and
   * the sea's shimmer are the same as the island's.
   */
  readonly illustration?: boolean
}

const inSelection = (selection: Selection | undefined, x: number, y: number): boolean =>
  selection !== undefined &&
  x >= selection.x &&
  x < selection.x + selection.w &&
  y >= selection.y &&
  y < selection.y + selection.h

/** The column and row markers a player reads a coordinate from (#54). */
function markers(count: number): TemplateResult[] {
  const marks: TemplateResult[] = []
  for (let n = 0; n < count; n += 1) marks.push(html`<span class="mark">${n}</span>`)
  return marks
}

/**
 * The grid itself.
 *
 * Pointer handling is delegated to the container rather than bound per cell:
 * two hundred and forty listeners re-bound on every step is a lot of work for
 * a screen that re-renders after every move.
 */
export function mapGrid(cells: readonly Cell[], options: GridOptions = {}): TemplateResult {
  const point = (event: PointerEvent, phase: 'down' | 'move' | 'up') => {
    if (options.onPoint === undefined) return
    const target = event.target as HTMLElement | null
    const x = Number(target?.dataset['x'])
    const y = Number(target?.dataset['y'])
    if (!Number.isInteger(x) || !Number.isInteger(y)) return
    if (phase !== 'move') event.preventDefault()
    options.onPoint(x, y, phase)
  }

  const frame = [
    'map-frame',
    options.small ? 'map-small' : '',
    options.illustration ? 'map-illustration' : '',
  ]
  return html`<div class=${frame.join(' ')}>
    ${
      options.illustration
        ? null
        : html`<div class="map-corner" aria-hidden="true"></div>
            <div class="map-cols" aria-hidden="true">${markers(width)}</div>
            <div class="map-rows" aria-hidden="true">${markers(height)}</div>`
    }
    <div
      class="map ${options.onPoint === undefined ? '' : 'map-pickable'}"
      aria-hidden="true"
      @pointerdown=${(event: PointerEvent) => point(event, 'down')}
      @pointermove=${(event: PointerEvent) => point(event, 'move')}
      @pointerup=${(event: PointerEvent) => point(event, 'up')}
    >
      ${cells.map((cell) => {
        const altered = options.altered?.get(`${cell.x},${cell.y}`)
        const glyph = altered ?? cell.glyph
        const classes = [
          'cell',
          GLYPH_CLASS[glyph] ?? '',
          cell.source === 'hearsay' ? 'cell-hearsay' : '',
          cell.source === 'unknown' ? 'cell-unknown' : '',
          cell.you ? 'cell-you' : '',
          cell.shore === true ? 'cell-shore' : '',
          inSelection(options.selection, cell.x, cell.y) ? 'cell-picked' : '',
          altered === undefined ? '' : 'cell-altered',
        ]
        return html`<span class=${classes.join(' ')} data-x=${cell.x} data-y=${cell.y}
          >${glyph}</span
        >`
      })}
    </div>
  </div>`
}

/**
 * The class that gives a grid its column count.
 *
 * A class rather than a `style` attribute, because the policy is
 * `style-src 'self'` and CSP blocks a style attribute in markup (Q22.1,
 * #119). lit-html's `styleMap` was tried first and does not help: it sets the
 * property through the CSSOM on every render *after* the first, and commits
 * the first as an attribute -- so under the tightened policy every slice was
 * blocked exactly once, on the only draw that mattered, and came out one
 * column wide.
 *
 * `app.css` carries one class per width up to the width of the grid, which is
 * as wide as any slice can be.
 */
export const columnsClass = (columns: number): string =>
  `cols-${Math.max(1, Math.min(Math.trunc(columns), width))}`

/** A slice's own little grid, as a letter shows it. */
export function sliceGrid(slice: api.ApiAttachments['slice']): TemplateResult | null {
  if (slice === undefined) return null
  return html`<div class="slice ${columnsClass(slice.w)}" aria-hidden="true">
    ${slice.glyphs.map(
      (glyph) => html`<span class="cell ${GLYPH_CLASS[glyph] ?? ''}">${glyph}</span>`,
    )}
  </div>`
}
